import { safeJSONParse } from 'utils/data/parse.utils';
import { parsePermName } from '../../utils/Roles.utils';
import { HierarchyMap, Role } from '../types/RolesTable.types';

// Count how many permissions are enabled for a role from its role_perms map
// ({ permName: boolean }). role_perms is granted-only but tolerant of `false`
// entries, so we count only truthy values. Accepts the raw JSON string (how the
// API returns the JSON column) or an already-parsed object. Returns null when no
// permissions payload is present so callers can show a placeholder, not a bogus 0.
export function countEnabledPermissions(
  rolePerms?: string | Record<string, unknown> | null,
): number | null {
  if (!rolePerms) return null;
  const parsed =
    typeof rolePerms === 'string'
      ? (safeJSONParse(rolePerms, null) as Record<string, unknown> | null)
      : rolePerms;
  if (!parsed || typeof parsed !== 'object') return null;
  return Object.values(parsed).filter(Boolean).length;
}

/** One row of the editor's permission list, as useRoleForm builds it from the meta endpoint. */
export type RoleEditorPermItem = {
  name: string;
  linked?: number;
  enableLinked?: number;
  index: number;
};

/**
 * The number the role editor shows for a role: every enabled permission key, plus each section
 * header (a row with `linked` children) whose children are all enabled — the editor derives a
 * header's checkbox exactly that way (PermissionsTab). Owner with all 116 keys therefore reads
 * 139, not 116 (Mike, 2026-08-28: "139 is correct, 113 is inaccurate for counting nested perms").
 * Without the editor's list it falls back to the plain key count.
 */
export function countEnabledPermissionsLikeEditor(
  rolePerms: string | Record<string, unknown> | null | undefined,
  permList: RoleEditorPermItem[] | null | undefined,
): number | null {
  if (!Array.isArray(permList) || permList.length === 0) return countEnabledPermissions(rolePerms);
  if (!rolePerms) return null;
  const parsed =
    typeof rolePerms === 'string'
      ? (safeJSONParse(rolePerms, null) as Record<string, unknown> | null)
      : rolePerms;
  if (!parsed || typeof parsed !== 'object') return null;

  const flags = permList.map(() => false);
  const indexByName = new Map(permList.map((item, index) => [item.name, index]));
  Object.entries(parsed).forEach(([key, value]) => {
    const index = indexByName.get(parsePermName(key));
    if (index !== undefined) flags[index] = Boolean(value);
  });
  // Headers, innermost first (a nested block has a higher index than its parent), so a parent
  // sees its nested header already resolved.
  for (let index = permList.length - 1; index >= 0; index -= 1) {
    const linked = permList[index].linked ?? 0;
    if (linked <= 0) continue;
    let hasChildren = false;
    let allEnabled = true;
    for (let child = index + 1; child <= index + linked && child < flags.length; child += 1) {
      hasChildren = true;
      if (!flags[child]) allEnabled = false;
    }
    if (hasChildren) flags[index] = allEnabled;
  }
  return flags.filter(Boolean).length;
}

// Search helpers
export function normalizeSearch(search?: string): string {
  return (search ?? '').toLowerCase().replace(/\s/g, '');
}

export function filterRoles(roles: Role[], search?: string): Role[] {
  if (!roles || roles.length === 0) return [];
  const s = normalizeSearch(search);
  if (!s) return roles;

  return roles.filter((r) => {
    const name = (r.role_name ?? '').toLowerCase().replace(/\s/g, '');
    const email = (r.created_by_email ?? '').toLowerCase().replace(/\s/g, '');
    return name.includes(s) || email.includes(s);
  });
}

// Pagination
export function paginate<T>(items: T[], page = 1, pageSize = 25) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize;
  const end = start + pageSize;
  return {
    items: items.slice(start, end),
    total,
    page: current,
    pageSize,
    totalPages,
  };
}

// Hierarchy utilities (migrated from RoleUtils)
export function computeRolesHierarchy(roles: Role[], username?: string): { hierarchy: HierarchyMap; yourHighest?: number } {
  const rolesToHierarch = [...roles].filter((item) => parseInt(String(item.role_Id)) >= 0);
  const hierarchy: HierarchyMap = {};
  rolesToHierarch.forEach((role) => {
    hierarchy[String(role.role_Id)] = role.hierarchy_level ?? 0;
  });

  let yourHighest = 999;
  if (username) {
    const myRoles = roles
      .filter((r) => r.users?.includes(username))
      .map((r) => r.role_Id);

    rolesToHierarch.forEach((role) => {
      if (myRoles.includes(role.role_Id)) {
        if ((role.hierarchy_level ?? 0) <= yourHighest) yourHighest = role.hierarchy_level ?? 0;
      }
    });
  }

  return { hierarchy, yourHighest };
}

export function moveRoleInHierarchy(hierarchy: HierarchyMap, fromId: string, toId: string, delta: number): HierarchyMap {
  const next = { ...hierarchy };
  if (next[fromId] === undefined || next[toId] === undefined) return next;
  next[fromId] += delta;
  next[toId] -= delta;
  return next;
}

export function compareRolesByHierarchy(a: Role, b: Role, hierarchy: HierarchyMap) {
  if (parseInt(String(a.role_Id)) === -2) return 1;
  if (parseInt(String(b.role_Id)) === -2) return -1;
  if (parseInt(String(a.role_Id)) === -1) return -1;
  if (parseInt(String(b.role_Id)) === -1) return 1;
  if (parseInt(String(a.role_Id)) < 0) return -1;
  if (parseInt(String(b.role_Id)) < 0) return 1;
  return (hierarchy[String(a.role_Id)] ?? 0) - (hierarchy[String(b.role_Id)] ?? 0);
}

// Sorting wrapper
export function sortRoles(roles: Role[], hierarchy: HierarchyMap): Role[] {
  if (!roles || roles.length === 0) return [];
  return [...roles].sort((a, b) => compareRolesByHierarchy(a, b, hierarchy));
}

export default {
  countEnabledPermissions,
  countEnabledPermissionsLikeEditor,
  normalizeSearch,
  filterRoles,
  paginate,
  computeRolesHierarchy,
  moveRoleInHierarchy,
  compareRolesByHierarchy,
  sortRoles,
};
